import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export type PlantillaCategoria = "certificado" | "checklist" | "boletin_manual";

export const PLANTILLA_CATEGORIAS: { value: PlantillaCategoria; label: string }[] = [
  { value: "certificado", label: "Certificados de instalación" },
  { value: "checklist", label: "Checklists de entrega" },
  { value: "boletin_manual", label: "Boletines / legalizaciones / manuales" },
];

export interface PlantillaDocumento {
  id: string;
  empresa_id: string;
  nombre: string;
  descripcion: string | null;
  categoria: PlantillaCategoria;
  etiqueta: string | null;
  nombre_archivo: string;
  url_archivo: string;
  tamano_archivo: number | null;
  subido_por: string;
  created_at: string;
  updated_at: string;
}

export const PLANTILLAS_BUCKET = "plantillas-documentos";

export function usePlantillasDocumentos() {
  const { perfil, user } = useAuth();
  const { toast } = useToast();
  const [plantillas, setPlantillas] = useState<PlantillaDocumento[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("plantillas_documentos")
      .select("*")
      .eq("empresa_id", perfil.empresa_id)
      .order("categoria", { ascending: true })
      .order("nombre", { ascending: true });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setPlantillas((data ?? []) as PlantillaDocumento[]);
    }
    setLoading(false);
  }, [perfil?.empresa_id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (input: {
    nombre: string;
    descripcion?: string;
    categoria: PlantillaCategoria;
    etiqueta?: string;
    file: File;
  }) => {
    if (!perfil?.empresa_id || !user?.id) throw new Error("Sin sesión");
    const safe = input.file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${perfil.empresa_id}/${Date.now()}_${safe}`;
    const { error: upErr } = await supabase.storage
      .from(PLANTILLAS_BUCKET)
      .upload(path, input.file);
    if (upErr) throw upErr;
    const { error: insErr } = await (supabase as any).from("plantillas_documentos").insert({
      empresa_id: perfil.empresa_id,
      nombre: input.nombre,
      descripcion: input.descripcion || null,
      categoria: input.categoria,
      etiqueta: input.etiqueta || null,
      nombre_archivo: input.file.name,
      url_archivo: path,
      tamano_archivo: input.file.size,
      subido_por: user.id,
    });
    if (insErr) throw insErr;
    await load();
  };

  const update = async (id: string, patch: Partial<Pick<PlantillaDocumento, "nombre" | "descripcion" | "categoria" | "etiqueta">>) => {
    const { error } = await (supabase as any)
      .from("plantillas_documentos")
      .update(patch)
      .eq("id", id);
    if (error) throw error;
    await load();
  };

  const remove = async (p: PlantillaDocumento) => {
    await supabase.storage.from(PLANTILLAS_BUCKET).remove([p.url_archivo]);
    const { error } = await (supabase as any)
      .from("plantillas_documentos")
      .delete()
      .eq("id", p.id);
    if (error) throw error;
    await load();
  };

  const download = async (p: PlantillaDocumento) => {
    const { data, error } = await supabase.storage
      .from(PLANTILLAS_BUCKET)
      .createSignedUrl(p.url_archivo, 3600, { download: p.nombre_archivo });
    if (error || !data) {
      toast({ title: "Error", description: error?.message, variant: "destructive" });
      return;
    }
    window.open(data.signedUrl, "_blank");
  };

  return { plantillas, loading, load, upload, update, remove, download };
}
